<template>
  <div class="oversea-list-container">
    <div class="page-header">
      <div class="breadcrumb">ນັກສຶກສາຕ່າງປະເທດ / <span>ລາຍຊື່ນັກສຶກສາຮຽນຕ່າງປະເທດ</span></div>
      <div class="header-content">
        <h1>
          ລາຍຊື່ນັກສຶກສາຮຽນຕ່າງປະເທດ
          <span v-if="consulateCountry" class="country-scope-badge">{{ consulateCountry }}</span>
        </h1>

        <div style="display:flex;gap:10px;align-items:center;">
          <button v-if="hasBit('student-oversea-new', 1)" class="btn-primary" @click="$router.push('/home/students/oversea/new')">
            <i class="fas fa-user-plus"></i> ລົງທະບຽນນັກສຶກສາໃໝ່
          </button>
          <button class="btn-export-emis" @click="exportExcel" :disabled="exporting">
            <i class="fas fa-file-excel"></i> {{ exporting ? 'ກຳລັງ Export...' : 'Export Excel' }}
          </button>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <div class="search-box">
          <div class="input-container">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              v-model="searchQuery"
              @keyup.enter="handleSearch"
              placeholder="ຄົ້ນຫາດ້ວຍ ຊື່, ເລກໜັງສືຜ່ານແດນ ຫຼື ລະຫັດນັກສຶກສາ..."
            >
          </div>
          <button class="btn-search" @click="handleSearch">ຄົ້ນຫາ</button>
        </div>
      </div>

      <div class="panel-body">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ລຳດັບ</th>
                <th>ລະຫັດນັກສຶກສາ</th>
                <th>ຊື່ ແລະ ນາມສະກຸນ</th>
                <th>ເພດ</th>
                <th>ວັນເດືອນປີເກີດ</th>
                <th>ປະເທດທີ່ໄປສຶກສາ</th>
                <th>ລະດັບການສຶກສາ</th>
                <th>ສະຖານະ</th>
                <th class="text-center">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(s, index) in students" :key="s._id">
                <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                <td><span class="asset-code">{{ s.student_code }}</span></td>
                <td class="asset-name">{{ s.full_name_lao }}</td>
                <td>{{ s.gender }}</td>
                <td>{{ formatDate(s.date_of_birth) }}</td>
                <td>{{ s.host_country }}</td>
                <td>{{ s.degree_level }}</td>
                <td>{{ statusLabel(s.student_status) }}</td>
                <td class="text-center">
                  <button class="panel-action" @click="handleView(s)" title="ເບິ່ງປະຫວັດ / ພິມ">👁️</button>
                  <button v-if="hasBit('student-oversea-new', 1)" class="panel-action" @click="handleEdit(s)" title="ແກ້ໄຂ">✏️</button>
                  <button v-if="hasBit('student-oversea-list', 2)" class="panel-action text-danger" @click="confirmDelete(s)" title="ລົບ">🗑️</button>
                </td>
              </tr>
              <tr v-if="!loading && students.length === 0">
                <td colspan="9" class="text-center py-4">ບໍ່ມີຂໍ້ມູນນັກສຶກສາ</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- ສ່ວນຄວບຄຸມ Pagination -->
      <div class="pagination-container">
        <div class="pagination-info">ສະແດງ {{ startItem }} - {{ endItem }} ຈາກທັງໝົດ {{ totalRows }} ລາຍການ</div>
        <div class="pagination-controls">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">«</button>
          <template v-for="(page, idx) in visiblePages">
            <span v-if="page === '...'" :key="'e' + idx" class="page-ellipsis">…</span>
            <button v-else :key="page"
                    class="page-btn"
                    :class="{ active: currentPage === page }"
                    @click="changePage(page)">{{ page }}</button>
          </template>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">»</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api';
import * as XLSX from 'xlsx';
import { studentStatusLabel } from '@/utils/overseaStatus';
import { getPaginationRange } from '@/utils/pagination';

export default {
  name: 'OverseaStudentListPage',
  data() {
    return {
      students: [],
      currentPage: 1,
      pageSize: 20,
      totalRows: 0,
      searchQuery: '',
      loading: false,
      exporting: false,
      user: {},
      permissionState: {},
      searchTimer: null
    };
  },
  watch: {
    // ຕິດຕາມການປ່ຽນແປງຂອງ searchQuery ແລະ ຄົ້ນຫາໂດຍອັດຕະໂນມັດ
    searchQuery() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      // ໜ່ວງເວລາ 500ms ກ່ອນຈະຄົ້ນຫາ (Debounce) ເພື່ອບໍ່ໃຫ້ API ເຮັດວຽກໜັກເກີນໄປໃນຂະນະທີ່ພິມ
      this.searchTimer = setTimeout(() => {
        this.handleSearch();
      }, 500);
    }
  },
  computed: {
    consulateCountry() {
      return this.user.org_role === 'consulate' ? (this.user.country || null) : null;
    },
    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize) || 1;
    },
    visiblePages() {
      return getPaginationRange(this.currentPage, this.totalPages);
    },
    startItem() {
      return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },
    endItem() {
      const end = this.currentPage * this.pageSize;
      return end > this.totalRows ? this.totalRows : end;
    }
  },
  mounted() {
    this.loadUserInfo();
    this.fetchRolePermissions(this.user.user_level);
    this.fetchStudents();
  },
  methods: {
    loadUserInfo() {
      const savedInfo = localStorage.getItem('user-info');
      if (savedInfo) {
        try {
          this.user = JSON.parse(savedInfo);
        } catch (e) {
          console.error('Error parsing user-info', e);
          this.user = { username: 'Guest' };
        }
      }
    },
    async fetchStudents() {
      this.loading = true;
      try {
        const response = await api.get('/oversea-students', {
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            search: this.searchQuery,
            host_country: this.consulateCountry
          }
        });
        if (response.data.code === 1) {
          this.students = response.data.rows;
          this.totalRows = response.data.totalRows || response.data.rows.length;
        }
      } catch (error) {
        console.error('Error fetching oversea students:', error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchStudents();
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('lo-LA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    },
    async confirmDelete(s) {
      if (confirm(`ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລົບນັກສຶກສາ: ${s.full_name_lao}?`)) {
        try {
          const response = await api.delete(`/del/oversea_students/${s._id}`);
          if (response.data.code === 1) {
            alert('ລົບຂໍ້ມູນສຳເລັດ');
            this.fetchStudents();
          }
        } catch (error) {
          alert('ເກີດຂໍ້ຜິດພາດໃນການລົບ');
        }
      }
    },
    handleEdit(s) {
      this.$router.push(`/home/students/oversea/edit/${s._id}`);
    },
    handleView(s) {
      this.$router.push(`/home/students/oversea/detail/${s._id}`);
    },
    statusLabel(status) {
      return studentStatusLabel(status);
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchStudents();
      }
    },
    async fetchRolePermissions(roleId) {
      try {
        const res = await api.get(`/getRowsInt/permissions/role_id/${roleId}`);
        if (res.data && res.data.length > 0) {
          this.permissionState = res.data[0].permissions || {};
        } else if (roleId === 1) {
          this.permissionState = { 'student-oversea-list': 7, 'student-oversea-new': 7 };
        }
      } catch (e) {
        console.error('Fetch permissions error:', e);
      }
    },
    hasBit(moduleKey, bit) {
      const val = this.permissionState[moduleKey] || 0;
      return (val & (1 << bit)) !== 0;
    },
    async exportExcel() {
      this.exporting = true;
      try {
        // ດຶງຂໍ້ມູນທັງໝົດຕາມເງື່ອນໄຂຄົ້ນຫາປັດຈຸບັນ (ບໍ່ມີ pagination) ສຳລັບ export
        const res = await api.get('/oversea-students', {
          params: { page: 1, limit: 9999, search: this.searchQuery, host_country: this.consulateCountry }
        });
        if (res.data.code === 1 && res.data.rows.length > 0) {
          const rows = res.data.rows.map((s, i) => ({
            'ລຳດັບ': i + 1,
            'ລະຫັດນັກສຶກສາ': s.student_code || '',
            'ຊື່ ແລະ ນາມສະກຸນ (ລາວ)': s.full_name_lao || '',
            'ຊື່ ແລະ ນາມສະກຸນ (ອັງກິດ)': s.full_name_eng || '',
            'ເພດ': s.gender || '',
            'ວັນເດືອນປີເກີດ': this.formatDate(s.date_of_birth),
            'ເລກໜັງສືຜ່ານແດນ': s.passport_number || '',
            'ປະເທດທີ່ໄປສຶກສາ': s.host_country || '',
            'ລະດັບການສຶກສາ': s.degree_level || '',
            'ສະຖາບັນການສຶກສາ': s.university_name || '',
            'ສາຂາວິຊາ': s.field_of_study || '',
            'ປະເພດທຶນການສຶກສາ': s.funding_type || '',
            'ສະຖານະ': this.statusLabel(s.student_status),
            'ສະຖານະການກວດສອບ': s.verification_status || ''
          }));
          const ws = XLSX.utils.json_to_sheet(rows);
          ws['!cols'] = Object.keys(rows[0]).map(key => ({ wch: Math.max(key.length * 1.6, 14) }));
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'OverseaStudents');
          const ts = new Date().toISOString().slice(0, 10);
          XLSX.writeFile(wb, `OverseaStudents_${ts}.xlsx`);
        } else {
          alert('ບໍ່ມີຂໍ້ມູນທີ່ຈະ Export');
        }
      } catch (e) {
        console.error('Export error:', e);
        alert('ເກີດຂໍ້ຜິດພາດໃນການ Export');
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.oversea-list-container { animation: fadeIn 0.4s ease; }
.header-content { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
.search-box { display: flex; gap: 10px; padding: 15px 20px; background: #fcfcfc; border-radius: 10px 10px 0 0; }
.input-container { position: relative; flex: 1; max-width: 400px; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); }
.input-container input { width: 100%; padding: 10px 10px 10px 35px; border: 1px solid var(--border); border-radius: 8px; font-size: 14px; }
.btn-search { background: var(--lao); color: white; border: none; padding: 0 20px; border-radius: 8px; cursor: pointer; font-weight: 500; }
.pagination-container { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; border-top: 1px solid var(--border); }
.page-btn { padding: 5px 12px; border: 1px solid var(--border); background: white; border-radius: 4px; cursor: pointer; margin: 0 2px; }
.page-btn.active { background: var(--lao); color: white; border-color: var(--lao); }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-ellipsis { padding: 5px 6px; color: var(--text3); }
.text-danger { color: var(--danger) !important; }
.country-scope-badge { font-size: 12px; font-weight: 600; background: var(--lao-light); color: var(--lao); padding: 3px 10px; border-radius: 12px; margin-left: 8px; vertical-align: middle; }
.text-center { text-align: center; }
.py-4 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
.btn-primary {
  background: var(--lao);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-primary:hover { background: var(--lao-mid); }
.btn-export-emis {
  background: #16a34a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  transition: 0.2s;
}
.btn-export-emis:hover { background: #15803d; }
.btn-export-emis:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
